import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../App.css";
import styles from "../styles/allUsers.module.css";
//import "bootstrap/dist/css/bootstrap.min.css";
import ChangeUserDataModal from "./modals/changeUserDataModal.tsx";
import ConfirmDeleteUserModal from "./modals/confirmDeleteUserModal.tsx";
import ConfirmHideModal from "./modals/confirmHideModal.tsx";
import ConfirmPermissionModal from "./modals/confirmPermissionModal.tsx";
import { User } from "./helpers/interfaces.tsx";
import { API_BASE_URL } from "../config.tsx";
import { authedFetch, AuthError } from "./helpers/requests.tsx";
import { useTranslation } from "react-i18next";
import { useAuth } from "../firebase/auth.tsx";
import { correctUserRoleName } from "./helpers/helpers.tsx";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string;
  permission?: boolean;
  scenarioId?: string;
  isHidden?: boolean;
}

const AllUsers = ({ userList, setUserList }) => {
  const { userId: currentUserId, logout, updateUser } = useAuth();
  const { headquarterId, groupId } = useParams();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonScenarios, setComingSoonScenarios] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(false);

  const [selectedUser, setSelectedUser] = useState<User>({});

  const [editingUsers, setEditingUsers] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [showConfirmDeleteUserModal, setShowConfirmDeleteUserModal] =
    useState(false);

  const [showChangeDataModal, setShowChangeDataModal] = useState(false);

  const [visibility, setVisibility] = useState({});
  const [showConfirmHideModal, setShowConfirmHideModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] =
    useState(false);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [selectedScenarioName, setSelectedScenarioName] = useState("");
  const [selectedTraining, setSelectedTraining] = useState<SelectedTraining>(
    {}
  );

  const [streamingClientIds, setStreamingClientIds] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const ws = new WebSocket(`${API_BASE_URL}/ws/`);

    ws.onopen = () => {
      ws.send("getStreamingRooms");
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        if (message.type === "clientList") {
          setStreamingClientIds(Object.keys(message.clients));
        }
      } catch (error) {}
    };

    const fetchData = async () => {
      setIsLoadingUsers(true);
      await getUsersList();
      setIsLoadingUsers(false);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      ws.close();

      setUserList([]);
      setSelectedUser({});
      setEditingUsers({});
      setTrainingsList([]);
    };
  }, [setUserList]);

  useEffect(() => {
    updateUserListWithStreamingStatus();
  }, [isLoadingUsers, streamingClientIds]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible((prevVisible) => !prevVisible);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const getUsersList = async () => {
    let requestTarget = "";
    let requestBody = null;

    if (headquarterId && !groupId) {
      requestTarget = "getUsersInHeadquarter";
      requestBody = JSON.stringify(headquarterId);
    } else if (groupId) {
      requestTarget = "getUsersInGroup";
      requestBody = JSON.stringify(groupId);
    } else {
      requestTarget = "getUsersList";
    }

    try {
      const users = await authedFetch(`api/requests/${requestTarget}`, {
        method: "POST",
        body: requestBody,
      });

      setUserList(users);
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error get users list:", error);
      }
    }
  };

  const updateUserListWithStreamingStatus = () => {
    setUserList((prevUserList) =>
      prevUserList.map((user) => ({
        ...user,
        isStreaming: streamingClientIds.includes(user.id),
      }))
    );
  };

  const checkUserVerification = async (email: string | null) => {
    try {
      const result = await authedFetch(`api/auth/checkUserVerification`, {
        method: "POST",
        body: JSON.stringify(email),
      });

      return result;
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error verify user:", error);
      }
    }
  };

  const verifyUser = async (userId: string | null) => {
    try {
      setIsUserVerified(true);

      const result = await authedFetch(`api/auth/verifyUser`, {
        method: "POST",
        body: JSON.stringify(userId),
      });

      return result;
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error verify user:", error);
      }
    }
  };

  const updatePermission = async (
    userId: string,
    trainingId: string,
    isChecked: boolean,
    isHidden: boolean = false
  ) => {
    try {
      await authedFetch(`api/requests/updateTrainingPermission`, {
        method: "POST",
        body: JSON.stringify({
          entityId: userId,
          entityType: "user",
          trainingId: trainingId,
          isChecked: isChecked,
          isHidden: isHidden,
        }),
      });
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error update training permission:", error);
      }
    }
  };

  const fetchTrainingsList = async (userId: string) => {
    setIsLoadingTrainings(true);

    const showAll = !headquarterId && !groupId;
    try {
      const trainings = await authedFetch(
        `api/requests/getTrainingsListWithPermissions`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: userId,
            entityType: "user",
            showAll: showAll,
          }),
        }
      );

      setTrainingsList(trainings);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        trainings.forEach((training) => {
          updatedVisibility[training.scenarioId] =
            updatedVisibility[training.scenarioId] || training.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error get trainings list:", error);
      }
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const fetchComingSoonScenarios = async (userId: string) => {
    setIsLoadingTrainings(true);

    const showAll = !headquarterId && !groupId;

    try {
      const scenarios = await authedFetch(`api/requests/getComingSoonScenarios`, {
        method: "POST",
        body: JSON.stringify({ entityId: userId, entityType: "user", showAll }),
      });

      setComingSoonScenarios(scenarios);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        scenarios.forEach((scenario) => {
          updatedVisibility[scenario.id] = scenario.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error get coming soon scenarios:", error);
      }
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleDeleteUser = async () => {
    setUserList((prevUserList) =>
      prevUserList.filter((user) => user.id !== selectedUser.id)
    );
    setSelectedUser({});
    setEditingUsers({});
    setTrainingsList([]);

    setShowConfirmDeleteUserModal(false);
  };

  const handleChangeData = async (updatedUser: User) => {
    // Update the user list
    setUserList((prevUserList) =>
      prevUserList.map((user) =>
        user.id === updatedUser.id ? { ...user, ...updatedUser } : user
      )
    );

    // Update the selected user state
    setSelectedUser((prevSelectedUser) =>
      prevSelectedUser.id === updatedUser.id
        ? { ...prevSelectedUser, ...updatedUser }
        : prevSelectedUser
    );

    // Update the user data in the AuthContext
    if (updatedUser.id === currentUserId) {
      updateUser(updatedUser);
    }

    // Update permissions rendering
    handleEditClick(updatedUser);

    setShowChangeDataModal(false);
  };

  const handleEditClick = async (user: User) => {
    setEditingUsers((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[user.id] = true;
      return newState;
    });

    const verification = await checkUserVerification(user.email);
    setIsUserVerified(verification);

    setSelectedUser(user);

    await fetchTrainingsList(user.id);
    await fetchComingSoonScenarios(user.id);
  };

  const handlePermissionChange = async (
    trainingId: string,
    targetId: string,
    permission: boolean
  ) => {
    await updatePermission(targetId, trainingId, permission);
    setTrainingsList(
      trainingsList.map((training) => {
        if (training.id === trainingId) {
          return { ...training, permission: permission };
        }

        return training;
      })
    );
    setSelectedTraining({});
  };

  const handleHideClick = (scenarioId) => {
    setShowConfirmHideModal(false);
    const isHidden = visibility[scenarioId];
    if (
      comingSoonScenarios.some(
        (comingSoonScenario) => comingSoonScenario.id === scenarioId
      )
    ) {
      // Update permissions to false for scenario
      const updatedComingSoonScenarios = comingSoonScenarios.map(
        (comingSoonScenario) => {
          if (comingSoonScenario.id === scenarioId) {
            updatePermission(
              selectedUser.id,
              comingSoonScenario.id,
              true,
              !isHidden
            );
            return { ...comingSoonScenario, isHidden: !isHidden };
          }
          return comingSoonScenario;
        }
      );

      setComingSoonScenarios(updatedComingSoonScenarios);
    } else {
      // Update permissions to false for all trainings under this scenario
      const updatedTrainings = trainingsList.map((training) => {
        if (training.scenarioId === scenarioId) {
          updatePermission(selectedUser.id, training.id, false, !isHidden);
          return { ...training, permission: false, isHidden: !isHidden };
        }
        return training;
      });

      setTrainingsList(updatedTrainings);
    }

    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [scenarioId]: !prevVisibility[scenarioId],
    }));
  };

  const handleStreamClick = (userId) => {
    navigate(`/stream/${userId}`);
  };

  const renderConfirmDeleteUserModal = () => {
    return (
      <div className={"adminContainer"}>
        <ConfirmDeleteUserModal
          setShowConfirmDeleteUserModal={setShowConfirmDeleteUserModal}
          user={selectedUser}
          handleDeleteUser={handleDeleteUser}
        />
      </div>
    );
  };

  const renderChangeDataModal = () => {
    return (
      <div className={"adminContainer"}>
        <ChangeUserDataModal
          setShowChangeDataModal={setShowChangeDataModal}
          user={selectedUser}
          handleChangeData={handleChangeData}
          adminView={!headquarterId && !groupId}
          headquarterView={!!headquarterId && !groupId}
          groupView={!!groupId}
        />
      </div>
    );
  };

  const renderConfirmHideModal = () => {
    const isHidden = visibility[selectedScenario];
    if (isHidden) handleHideClick(selectedScenario);
    else {
      return (
        <ConfirmHideModal
          setShowConfirmHideModal={setShowConfirmHideModal}
          scenario={selectedScenario}
          scenarioName={selectedScenarioName}
          usersView={true}
          handleHideClick={handleHideClick}
        />
      );
    }
  };

  const renderConfirmPermissionModal = () => {
    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={setShowConfirmPermissionModal}
        trainingId={selectedTraining.id}
        trainingName={selectedTraining.name}
        targetId={selectedTraining.entityId}
        targetName={selectedUser.firstName + " " + selectedUser.lastName}
        permission={selectedTraining.permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  const groupTrainingsByScenario = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      const {
        scenarioId,
        scenarioName,
        name,
        id,
        permission,
        entityId,
        isHidden,
      } = training;
      if (!groupedTrainings[scenarioId]) {
        groupedTrainings[scenarioId] = {
          scenarioName: scenarioName,
          trainings: [],
        };
      }
      groupedTrainings[scenarioId].trainings.push({
        id: id,
        entityId: entityId,
        name: name,
        permission: permission,
        isHidden: isHidden,
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings: {
    [scenarioId: string]: {
      scenarioName: string;
      trainings: {
        id: string;
        entityId: string;
        name: string;
        permission: boolean;
        isHidden: boolean;
      }[];
    };
  }) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([scenarioId, { scenarioName, trainings }]) => {
            const isAnyTrainingHidden = trainings.some(
              (training) => training.isHidden
            );
            return (
              <div key={scenarioId} className={styles.trainingsGroup}>
                <label
                  className={styles.trainingsGroupTitle}
                  style={{ color: isAnyTrainingHidden ? "gray" : "#183a68" }}
                >
                  {scenarioName}
                </label>
                {!headquarterId && !groupId && (
                  <span
                    onClick={() => {
                      setSelectedScenario(scenarioId);
                      setSelectedScenarioName(scenarioName);
                      setShowConfirmHideModal(true);
                    }}
                    style={{
                      marginLeft: "1vh",
                      fontSize: "0.7em",
                      cursor: "pointer",
                    }}
                  >
                    {isAnyTrainingHidden ? t("show") : t("hide")}
                  </span>
                )}
                <ul>
                  {trainings.map((training) => (
                    <li
                      key={training.id}
                      className="form-check custom-checkbox"
                      style={{ color: training.isHidden ? "gray" : "#212529" }}
                    >
                      <label className="custom-checkbox-label">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="agreeCheckbox"
                          checked={training.permission}
                          //disabled={training.isHidden}
                          onChange={(e) => {
                            setSelectedTraining((prevInfo) => ({
                              ...prevInfo,
                              id: training.id,
                              name: training.name,
                              entityId: training.entityId,
                              permission: training.permission,
                            }));
                            setShowConfirmPermissionModal(true);
                          }}
                        />
                        <span className="checkmark"></span>
                        <span className="form-check-label">
                          {training.name}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
        )}
        {comingSoonScenarios.length > 0 && (
          <label
            className={styles.trainingsGroupTitle}
            style={{ color: "#183a68" }}
          >
            {t("Coming_soon")}:
          </label>
        )}
        {comingSoonScenarios.map(({ id, name, isHidden }) => {
          return (
            <div key={id} className={styles.trainingsGroup}>
              <label
                className={styles.trainingsGroupTitle}
                style={{ color: isHidden ? "gray" : "#212529" }}
              >
                {name}
              </label>
              {!headquarterId && !groupId && (
                <span
                  onClick={() => {
                    setSelectedScenario(id);
                    setSelectedScenarioName(name);
                    setShowConfirmHideModal(true);
                  }}
                  style={{
                    marginLeft: "1vh",
                    fontSize: "0.7em",
                    cursor: "pointer",
                  }}
                >
                  {isHidden ? t("show") : t("hide")}
                </span>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderUserRole = (role) => {
    if (role === "user" || role === "guest") return "";

    return (
      <span
        style={{
          color: "#183a68",
          marginLeft: "1vh",
        }}
      >
        | {correctUserRoleName(role, t)}
      </span>
    );
  };

  const renderUserGroupCountry = (countryCode) => {
    return (
      <span
        style={{
          color: "#183a68",
          marginLeft: "1vh",
        }}
      >
        | {t("country_" + countryCode)}
      </span>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoadingUsers ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            {/* Separate table header - don't find other way to avoid overlaping header on scroll */}
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: `calc(100%)` }}>
                      {t("User")}
                    </th>
                    {!headquarterId && !groupId && (
                      <th scope="col" style={{ width: `calc(100% / 3)` }}>
                        {t("Headquarter")}
                      </th>
                    )}
                    {!groupId && (
                      <th scope="col" style={{ width: `calc(100% / 3)` }}>
                        {t("Group")}
                      </th>
                    )}
                    <th scope="col" style={{ width: `calc(100% / 3)` }}></th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div className={styles.tableContent}>
              <div className={`container-fluid ${styles.containerFluid}`}>
                <table className="table">
                  <tbody>
                    {userList.map((user: User, index: number) => (
                      <tr
                        key={user.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#dbdfe7" : "transparent",
                        }}
                      >
                        <td style={{ width: `calc(100%)` }}>
                          <div className={styles.containerHeader}>
                            <img
                              src="/images/Icon_User_Simple.png"
                              style={{
                                width: "2.2vh",
                                marginLeft: "0.3vh",
                                marginRight: "1.5vh",
                              }}
                            />
                            <div className={styles.userDataWrapper}>
                              <div className={styles.userDataName}>
                                {user.firstName} {user.lastName}
                                {renderUserRole(user.role)}
                              </div>
                              <div className={styles.userDataEmail}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        {!headquarterId && !groupId && (
                          <td style={{ width: `calc(100% / 3)` }}>
                            {user.headquarterName}
                          </td>
                        )}
                        {!groupId && (
                          <td style={{ width: `calc(100% / 3)` }}>
                            {user.groupName}
                            {user.groupName && (
                              <span
                                style={{
                                  color: "#183a68",
                                }}
                              >
                                {renderUserGroupCountry(user.groupCountryCode)}
                              </span>
                            )}
                          </td>
                        )}
                        <td
                          style={{
                            color: "#183a68",
                            textAlign: "center",
                            width: `calc(100% / 3)`,
                          }}
                        >
                          {user.isStreaming && (
                            <>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "20px",
                                  textAlign: "center",
                                }}
                              >
                                {isVisible
                                  ? String.fromCharCode(0x2b24)
                                  : "\u2003"}
                              </span>
                              <span>{t("Streaming")}</span>
                            </>
                          )}
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleEditClick(user)}
                            style={{
                              backgroundColor: editingUsers[user.id]
                                ? "#404040"
                                : "#183a68",
                              borderColor: editingUsers[user.id]
                                ? "#404040"
                                : "#183a68",
                            }}
                          >
                            {t("Details")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedUser.id ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <img
                      className={styles.userDataImage}
                      src="/images/Icon_User_Simple.png"
                    />
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        {selectedUser.firstName + " " + selectedUser.lastName}
                      </div>
                      <div className={styles.userDataEmail}>
                        {selectedUser.email}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {!isUserVerified && (
                      <>
                        <button
                          type="button"
                          className={`${styles.buttonVerify} btn btn-primary`}
                          onClick={() => verifyUser(selectedUser.id)}
                        >
                          {t("Verify")}
                        </button>
                      </>
                    )}
                    {selectedUser.isStreaming && (
                      <>
                        <button
                          type="button"
                          className={`${styles.buttonStream} btn btn-primary`}
                          onClick={() => handleStreamClick(selectedUser.id)}
                        >
                          {t("Stream")}
                        </button>
                      </>
                    )}
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#183a68",
                          borderColor: "#183a68",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_User")}
                          </button>

                          {selectedUser.id !== currentUserId && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsOptionsOpen(false);
                                setShowConfirmDeleteUserModal(true);
                              }}
                              className="btn"
                            >
                              <img src="/images/Icon_Delete.png" />
                              {t("Delete_User")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  <p className={styles.containerHeaderTitle}>
                    {t("Training_permission")}
                  </p>
                  <p className={styles.containerHeaderDescription}>
                    {t(
                      "Set_user_access_to_these_trainings_in_the_VR_application"
                    )}
                  </p>
                </div>

                <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(
                      groupTrainingsByScenario(trainingsList)
                    )
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>{t("Select_user_to_edit")}</p>
            )}
          </div>
        </>
      )}
      {(showConfirmDeleteUserModal && renderConfirmDeleteUserModal()) ||
        (showChangeDataModal && renderChangeDataModal()) ||
        (showConfirmHideModal && renderConfirmHideModal()) ||
        (showConfirmPermissionModal && renderConfirmPermissionModal())}
    </div>
  );
};
export default AllUsers;
